import { useQuery } from '@tanstack/react-query';
import { insightsApi } from '@/lib/insightsApi';
import { api } from '@/lib/api';

const STALE = 5 * 60 * 1000; // 5 minutes

export function useHealthScore(accountId: number) {
  return useQuery({
    queryKey: ['health-score', accountId],
    queryFn: async () => (await insightsApi.healthScore(accountId)).data.data,
    staleTime: STALE,
    enabled: accountId > 0,
  });
}

export function useBudgetPacing(accountId: number) {
  return useQuery({
    queryKey: ['budget-pacing', accountId],
    queryFn: async () => (await insightsApi.budgetPacing(accountId)).data.data,
    staleTime: STALE,
    enabled: accountId > 0,
  });
}

export function useRoas(accountId: number) {
  return useQuery({
    queryKey: ['roas', accountId],
    queryFn: async () => (await insightsApi.roas(accountId)).data.data,
    staleTime: STALE,
    enabled: accountId > 0,
  });
}

export function useBiddingRecommendation(accountId: number) {
  return useQuery({
    queryKey: ['bidding-recommendation', accountId],
    queryFn: async () => (await insightsApi.biddingRecommendation(accountId)).data.data,
    staleTime: STALE,
    enabled: accountId > 0,
  });
}

export function useDiagnosticsPriority(accountId: number) {
  return useQuery({
    queryKey: ['diagnostics-priority', accountId],
    queryFn: async () => (await insightsApi.diagnosticsPriority(accountId)).data.data,
    staleTime: STALE,
    enabled: accountId > 0,
  });
}

export function useAnomalyDetect(accountId: number) {
  return useQuery({
    queryKey: ['account-alerts', accountId],
    queryFn: async () => {
      const res = await api.get(`/accounts/${accountId}/findings`, {
        params: { severity: 'high', status: 'open' },
      });
      const findings = (res.data?.data?.data ?? []) as Array<{
        id: number;
        severity: 'low' | 'medium' | 'high';
        rule_code?: string;
        message?: string;
        entity_type?: string;
      }>;
      return {
        detected: findings.length > 0,
        anomalies: findings.map((f) => ({
          metric: f.rule_code ?? f.entity_type ?? 'finding',
          z_score: 0,
          severity: f.severity,
          direction: 'up' as const,
          description: f.message ?? '',
        })),
      };
    },
    staleTime: STALE,
    enabled: accountId > 0,
  });
}

export function useAgencyProfitability() {
  return useQuery({
    queryKey: ['agency-profitability'],
    queryFn: async () => (await insightsApi.agencyProfitability()).data.data,
    staleTime: STALE,
  });
}

export function useAgencyWorkload() {
  return useQuery({
    queryKey: ['agency-workload'],
    queryFn: async () => (await insightsApi.agencyWorkload()).data.data,
    staleTime: STALE,
  });
}

export function useAgencySla() {
  return useQuery({
    queryKey: ['agency-sla'],
    queryFn: async () => (await insightsApi.agencySla()).data.data,
    staleTime: STALE,
  });
}

export function useAgencyChurnRisk() {
  return useQuery({
    queryKey: ['agency-churn-risk'],
    queryFn: async () => (await insightsApi.agencyChurnRisk()).data.data,
    staleTime: STALE,
  });
}
